<template>
  <v-menu
    v-model="menuOpen"
    offset-y
    transition="slide-y-transition"
    :close-on-content-click="false"
    dense
    :data-testid="`client-filter-menu--${filterType}`"
  >
    <!-- Activator -->
    <template v-slot:activator="{ on, attrs }">
      <v-btn icon small v-bind="attrs" v-on="on" class="ml-1" :data-testid="`client-filter-menu__btn--${filterType}`">
      <img
        src="/img/icons/FilterIcon.svg"
        alt="Filter Icon"
        :class="{ 'filter-icon--active': selectedValues.length > 0 }"
        class="filter-icon"
      />
    </v-btn>
    </template>

    <!-- Card -->
    <v-card class="client-filter-card">
      <!-- Header -->
      <v-card-title class="client-filter-header">
        <span class="header-title">{{ title }}</span>
        <v-btn icon small @click="menuOpen = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Search -->
      <v-card-text class="pa-2 pb-0">
        <v-text-field
          v-model="searchQuery"
          placeholder="Search..."
          dense
          outlined
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          :data-testid="`client-filter-menu__search--${filterType}`"
        ></v-text-field>
      </v-card-text>

      <!-- Table -->
      <v-simple-table class="ma-2 client-simple-table">
        <thead class="tableHeaderBg">
          <tr>
            <th class="checkbox-col">
              <v-checkbox
                hide-details
                class="client-checkbox"
                :input-value="isAllSelected"
                @change="toggleSelectAll"
                :data-testid="`client-filter-menu__select-all--${filterType}`"
              />
            </th>
            <th class="client-info-col">Select All ({{ filteredOptions.length }})</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="option in filteredOptions"
            :key="option.value"
            class="client-row"
          >
            <td class="checkbox-col">
              <v-checkbox
                hide-details
                class="client-checkbox"
                :input-value="isValueSelected(option.value)"
                @change="toggleValue(option.value)"
              />
            </td>
            <td class="client-info-col">
              <div class="client-info">
                <span class="client-text">{{ option.text }}</span>
              </div>
            </td>
          </tr>
          <tr v-if="filteredOptions.length === 0">
            <td colspan="2" class="text-center py-3 grey--text">
              No results found
            </td>
          </tr>
        </tbody>
      </v-simple-table>

      <!-- Footer -->
      <v-divider></v-divider>
      <v-card-actions class="client-footer">
        <v-btn text small @click="clearAll" v-if="pendingValues.length > 0">
          Clear All
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn class="primary" @click="applyFilters" :data-testid="`client-filter-menu__apply-btn--${filterType}`">
          Apply
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script>
export default {
  name: 'ClientFilterMenu',

  props: {
    /**
     * Type of filter: 'code' or 'name'
     */
    filterType: {
      type: String,
      required: true,
      validator: (value) => ['code', 'name'].includes(value)
    },
    /**
     * Title to display in header
     */
    title: {
      type: String,
      default: 'Filter'
    },
    /**
     * Currently selected values
     */
    selectedValues: {
      type: Array,
      default: () => []
    },
    /**
     * Available options to choose from
     * Each option should have { text, value }
     */
    options: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      menuOpen: false,
      searchQuery: '',
      pendingValues: []
    };
  },

  watch: {
    menuOpen(isOpen) {
      if (isOpen) {
        // Initialize pending values from current selection
        this.pendingValues = (this.selectedValues || []).slice();
        this.searchQuery = '';
      }
    }
  },

  computed: {
    /**
     * Filter options based on search query
     */
    filteredOptions() {
      if (!this.searchQuery) {
        return this.options;
      }
      const query = this.searchQuery.toLowerCase();
      return this.options.filter(opt => 
        opt.text.toLowerCase().includes(query) ||
        (opt.value && opt.value.toString().toLowerCase().includes(query))
      );
    },

    /**
     * Check if all filtered options are selected
     */
    isAllSelected() {
      if (this.filteredOptions.length === 0) return false;
      return this.filteredOptions.every(opt => 
        this.pendingValues.includes(opt.value)
      );
    }
  },

  methods: {
    /**
     * Toggle select all for filtered options
     */
    toggleSelectAll() {
      if (this.isAllSelected) {
        // Deselect all filtered options
        const filteredVals = this.filteredOptions.map(o => o.value);
        this.pendingValues = this.pendingValues.filter(v => !filteredVals.includes(v));
      } else {
        // Select all filtered options
        const filteredVals = this.filteredOptions.map(o => o.value);
        const uniqueVals = new Set([...this.pendingValues, ...filteredVals]);
        this.pendingValues = Array.from(uniqueVals);
      }
    },

    /**
     * Check if a value is selected
     */
    isValueSelected(value) {
      return this.pendingValues.includes(value);
    },

    /**
     * Toggle a single value
     */
    toggleValue(value) {
      const index = this.pendingValues.indexOf(value);
      if (index > -1) {
        this.pendingValues.splice(index, 1);
      } else {
        this.pendingValues.push(value);
      }
    },

    /**
     * Clear all selections
     */
    clearAll() {
      this.pendingValues = [];
    },

    /**
     * Apply filters and emit update event
     */
    applyFilters() {
      this.$emit('update:selected-values', this.pendingValues.slice());
      this.menuOpen = false;
    }
  }
};
</script>

<style scoped>
/* Card */
.client-filter-card {
  width: 320px;
  border-radius: 10px;
  overflow: hidden;
}

/* Header */
.client-filter-header {
  background-color: #e1e1e1;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

/* Table */
.client-simple-table {
  max-height: 300px;
  overflow-y: auto;
}

/* Rows */
.client-row {
  border-bottom: 1px solid #e0e0e0;
}

.client-row:hover {
  background-color: #f7f7f7;
}

/* Checkbox */
.client-checkbox {
  margin: 0;
  padding: 0;
}

/* Client info */
.client-info {
  display: flex;
  align-items: center;
}

.client-text {
  font-size: 13px;
  color: #333;
}

/* Footer */
.client-footer {
  padding: 12px;
}

/* Filter icon */
.filter-icon {
  width: 16px;
  height: 16px;
  opacity: 0.5;
  transition: opacity 0.2s ease;
}

.filter-icon--active {
  opacity: 1;
  filter: brightness(0) saturate(100%) invert(15%) sepia(20%) saturate(1000%) hue-rotate(160deg) brightness(90%) contrast(95%);
}
</style>
